/**
 * BrainSoulPage — Brain Soul + 心跳历史 + 自我评估
 *
 * Tabs:
 *   - Soul：当前状态、松散端、学习模式、自动化建议
 *   - 心跳历史：最近心跳分析结果
 *   - 周报：L8 自我评估周报
 *
 * API endpoints:
 *   GET  /api/v1/brain/soul
 *   POST /api/v1/brain/soul/loose-ends
 *   POST /api/v1/brain/soul/suggestions/:id/respond
 *   GET  /api/v1/brain/soul/heartbeats
 *   POST /api/v1/brain/soul/heartbeat     (手动触发)
 *   GET  /api/v1/brain/reports/weekly
 *   GET  /api/v1/brain/reports/weekly/history
 *   POST /api/v1/brain/proactive/run
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  RefreshCw, Loader2, Brain, Activity, BookOpen, Lightbulb,
  CheckCircle2, XCircle, Plus, Zap, BarChart3,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════

interface LooseEnd {
  id: string;
  item: string;
  priority: 'high' | 'medium' | 'low';
  relatedRef?: string;
  createdAt: string;
  resolvedAt?: string;
}

interface AutomationSuggestion {
  id: string;
  name: string;
  skillName: string;
  reason: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
}

interface LearnedPattern {
  id?: string;
  pattern: string;
  evidence?: string;
  confidence?: number;
  lastSeenAt?: string;
  firstSeenAt?: string;
}

interface BrainSoul {
  id: string;
  tenantId: string;
  tenantName?: string;
  tenantSlug?: string;
  currentFocus?: string;
  preferences?: string;
  looseEnds: LooseEnd[];
  recurringChores: unknown[];
  learnedPatterns: (string | LearnedPattern)[];
  suggestedAutomations: AutomationSuggestion[];
  updatedAt: string;
}

interface HeartbeatRecord {
  id: string;
  tenantId: string;
  analysis: {
    overallHealth: string;
    insights: string[];
    priorities: string[];
    suggestedActions: string[];
  };
  createdAt: string;
}

interface WeeklyReport {
  tenantId: string;
  weekStart: string;
  metrics: {
    automationsRan: number;
    automationsSuccess: number;
    automationsFailed: number;
    heartbeatsRan: number;
    looseEndsCreated: number;
    looseEndsResolved: number;
  };
  summaryLlm: string;
  recommendations: string;
}

interface GuardianSummary {
  activeGoals: number;
  atRiskGoals: number;
  achievedGoals: number;
  openWatchItems: number;
  escalatedWatchItems: number;
  lastCheckpointAt: string | null;
  lastHeartbeatAt: string | null;
  goalStatusCounts?: Record<string, number>;
}

interface GuardianGoal {
  id: string;
  goalCode: string;
  goalName: string;
  goalNameZh?: string | null;
  goalType: string;
  metricKey: string;
  metricUnit?: string | null;
  targetValue: number | string;
  targetValueMax?: number | string | null;
  targetDirection: string;
  currentValue?: number | string | null;
  alertThreshold?: number | string | null;
  progressStatus?: string | null;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  lastCheckedAt?: string | null;
  notes?: string | null;
  isActive: boolean;
  metadata?: Record<string, unknown>;
  updatedAt?: string;
}

interface GuardianWatchItem {
  id: string;
  watchType: string;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  entityType?: string | null;
  entityId?: string | null;
  entityRef?: string | null;
  goalId?: string | null;
  title: string;
  description?: string | null;
  status: string;
  resolution?: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  escalateAfter?: string | null;
  checkCount: number;
  actionCount: number;
  source?: string | null;
  metadata?: Record<string, unknown> | null;
  evidence?: Record<string, unknown> | null;
  updatedAt?: string;
}

interface GuardianCheckpoint {
  id: string;
  goalId: string;
  goalCode?: string | null;
  goalName?: string | null;
  goalNameZh?: string | null;
  actualValue: number | string;
  targetValue: number | string;
  gap?: number | string | null;
  gapPct?: number | string | null;
  status: string;
  context?: Record<string, unknown> | null;
  triggeredBy: string;
  recordedAt: string;
}

interface GuardianAction {
  id: string;
  goalId: string;
  goalCode?: string | null;
  goalName?: string | null;
  goalNameZh?: string | null;
  actionType: string;
  skillName?: string | null;
  description: string;
  result?: Record<string, unknown> | null;
  createdAt: string;
}

interface GuardianData {
  tenantId: string;
  tenantName?: string;
  tenantSlug?: string;
  guardianAvailable: boolean;
  summary: GuardianSummary;
  goals: GuardianGoal[];
  watchItems: GuardianWatchItem[];
  recentCheckpoints: GuardianCheckpoint[];
  recentActions: GuardianAction[];
}

// ═══════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════

function ageFromNow(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  return `${Math.floor(min / 60)}h ago`;
}

function buildBrainApiPath(tenantId: string | null, suffix: string): string {
  return tenantId ? `/platform/tenants/${tenantId}/brain${suffix}` : `/brain${suffix}`;
}

function buildBrainReportApiPath(tenantId: string | null, suffix: string): string {
  return tenantId ? `/platform/tenants/${tenantId}/brain/reports${suffix}` : `/brain/reports${suffix}`;
}

function formatMetricValue(value: string | number | null | undefined, unit?: string | null): string {
  if (value == null || value === '') return '—';
  const numeric = Number(value);
  const text = Number.isFinite(numeric)
    ? (Math.abs(numeric) >= 1000 ? numeric.toLocaleString() : `${numeric}`)
    : String(value);
  return unit ? `${text} ${unit}` : text;
}

function PriorityBadge({ priority }: { priority: LooseEnd['priority'] }) {
  const map = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  };
  const labels = { high: '高', medium: '中', low: '低' };
  return (
    <span className={`text-xs border rounded px-1.5 py-0.5 ${map[priority]}`}>
      {labels[priority]}
    </span>
  );
}

function HealthBadge({ health }: { health: string }) {
  const map: Record<string, string> = {
    healthy: 'bg-emerald-600 hover:bg-emerald-700',
    degraded: 'bg-yellow-500 hover:bg-yellow-600',
    critical: 'bg-red-600 hover:bg-red-700',
  };
  return <Badge className={`${map[health] ?? ''} text-xs`}>{health}</Badge>;
}

function GoalStatusBadge({ status }: { status: string | null | undefined }) {
  const normalized = status ?? 'PENDING';
  const map: Record<string, string> = {
    ACHIEVED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    ON_TRACK: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    AT_RISK: 'bg-amber-100 text-amber-700 border-amber-200',
    BEHIND: 'bg-orange-100 text-orange-700 border-orange-200',
    FAILED: 'bg-red-100 text-red-700 border-red-200',
    PENDING: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  };
  return <span className={`text-xs border rounded px-1.5 py-0.5 ${map[normalized] ?? map.PENDING}`}>{normalized}</span>;
}

function WatchPriorityBadge({ priority }: { priority: GuardianWatchItem['priority'] }) {
  const map: Record<GuardianWatchItem['priority'], string> = {
    CRITICAL: 'bg-red-100 text-red-700 border-red-200',
    HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
    MEDIUM: 'bg-amber-100 text-amber-700 border-amber-200',
    LOW: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  };
  return <span className={`text-xs border rounded px-1.5 py-0.5 ${map[priority]}`}>{priority}</span>;
}

type TabId = 'soul' | 'guardian' | 'heartbeat' | 'report';

// ═══════════════════════════════════════════════════════════════
//  Soul Tab
// ═══════════════════════════════════════════════════════════════

function SoulTab({ soul, onRefresh, readOnly = false }: { soul: BrainSoul; onRefresh: () => void; readOnly?: boolean }) {
  const [newItem, setNewItem] = useState('');
  const [addingLooseEnd, setAddingLooseEnd] = useState(false);
  const [triggeringProactive, setTriggeringProactive] = useState(false);

  const handleAddLooseEnd = async () => {
    if (!newItem.trim()) return;
    setAddingLooseEnd(true);
    try {
      await api.post('/brain/soul/loose-ends', { item: newItem.trim(), priority: 'medium' });
      setNewItem('');
      onRefresh();
    } finally {
      setAddingLooseEnd(false);
    }
  };

  const handleRespondSuggestion = async (id: string, status: 'accepted' | 'rejected') => {
    await api.post(`/brain/soul/suggestions/${id}/respond`, { status });
    onRefresh();
  };

  const handleProactiveRun = async () => {
    setTriggeringProactive(true);
    try {
      await api.post('/brain/proactive/run', { maxActions: 3 });
      onRefresh();
    } finally {
      setTriggeringProactive(false);
    }
  };

  const openLooseEnds = soul.looseEnds.filter((le) => !le.resolvedAt);
  const pendingSuggestions = soul.suggestedAutomations.filter((s) => s.status === 'pending');

  return (
    <div className="space-y-6">
      {readOnly && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              当前为平台管理员跨租户查看模式。Soul 仍可查看，但不会在这里直接执行主动动作或改写租户待办。
            </p>
          </CardContent>
        </Card>
      )}

      {/* Current Focus */}
      {soul.currentFocus && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Brain className="h-4 w-4 text-purple-500" />当前关注
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{soul.currentFocus}</p>
            <p className="text-xs text-muted-foreground mt-1">更新于 {ageFromNow(soul.updatedAt)}</p>
          </CardContent>
        </Card>
      )}

      {/* Loose Ends */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Activity className="h-4 w-4 text-orange-500" />
              未解决事项 ({openLooseEnds.length})
            </CardTitle>
            {!readOnly && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1 text-xs"
                disabled={triggeringProactive}
                onClick={() => void handleProactiveRun()}
              >
                {triggeringProactive
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <Zap className="h-3 w-3 text-yellow-500" />
                }
                主动执行
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {openLooseEnds.length === 0 && (
            <p className="text-xs text-muted-foreground">暂无未解决事项 🎉</p>
          )}
          {openLooseEnds.map((le) => (
            <div key={le.id} className="flex items-start gap-2 border rounded p-2">
              <PriorityBadge priority={le.priority} />
              <p className="text-sm flex-1">{le.item}</p>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{ageFromNow(le.createdAt)}</span>
            </div>
          ))}
          {/* Add loose end */}
          {!readOnly && (
            <div className="flex gap-2 pt-1">
              <Input
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                placeholder="添加新事项..."
                className="h-8 text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter') void handleAddLooseEnd(); }}
              />
              <Button
                size="sm"
                className="h-8 gap-1"
                disabled={!newItem.trim() || addingLooseEnd}
                onClick={() => void handleAddLooseEnd()}
              >
                {addingLooseEnd ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Automation Suggestions */}
      {pendingSuggestions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Lightbulb className="h-4 w-4 text-yellow-500" />
              自动化建议 ({pendingSuggestions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingSuggestions.map((s) => (
              <div key={s.id} className="border rounded p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{s.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Skill: <span className="font-mono">{s.skillName}</span>
                    </p>
                    <p className="text-xs mt-1">{s.reason}</p>
                  </div>
                  {!readOnly && (
                    <div className="flex gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        className="h-7 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => void handleRespondSuggestion(s.id, 'accepted')}
                      >
                        <CheckCircle2 className="h-3 w-3" />接受
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 text-xs"
                        onClick={() => void handleRespondSuggestion(s.id, 'rejected')}
                      >
                        <XCircle className="h-3 w-3" />拒绝
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Learned Patterns */}
      {soul.learnedPatterns.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <BookOpen className="h-4 w-4 text-blue-500" />
              学习模式 ({soul.learnedPatterns.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {soul.learnedPatterns.slice(-10).reverse().map((pattern, idx) => (
                <div key={idx} className="text-xs border rounded p-2 bg-muted/40 font-mono whitespace-pre-wrap">
                  {typeof pattern === 'string' ? pattern : pattern.pattern}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Heartbeat Tab
// ═══════════════════════════════════════════════════════════════

function GuardianTab({ tenantId }: { tenantId: string | null }) {
  const [data, setData] = useState<GuardianData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<GuardianData>(buildBrainApiPath(tenantId, '/guardian'));
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Guardian 加载失败');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  if (error) {
    return <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded p-3">{error}</div>;
  }

  if (!data?.guardianAvailable) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            当前租户尚未提供 Guardian 目标表，或相关迁移未生效。可以先落正式 goals，再回来查看这里。
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => void load()} disabled={loading}>
          <RefreshCw className="h-4 w-4" />刷新 Guardian
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {[
          { label: '激活目标', value: data.summary.activeGoals, note: `达成/在轨 ${data.summary.achievedGoals}` },
          { label: '风险目标', value: data.summary.atRiskGoals, note: data.summary.goalStatusCounts ? `BEHIND/FAILED/AT_RISK` : '—' },
          { label: '开放观察项', value: data.summary.openWatchItems, note: `升级 ${data.summary.escalatedWatchItems}` },
          { label: '最近检查', value: ageFromNow(data.summary.lastCheckpointAt), note: `心跳 ${ageFromNow(data.summary.lastHeartbeatAt)}` },
        ].map((item) => (
          <Card key={item.label}>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p className="text-2xl font-semibold mt-1">{item.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{item.note}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-1.5">
            <Zap className="h-4 w-4 text-yellow-500" />业务目标
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.goals.length === 0 && <p className="text-sm text-muted-foreground">暂无业务目标。</p>}
          {data.goals.map((goal) => (
            <div key={goal.id} className="border rounded p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-sm">{goal.goalNameZh?.trim() || goal.goalName}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{goal.goalCode} · {goal.metricKey}</p>
                </div>
                <GoalStatusBadge status={goal.progressStatus} />
              </div>
              <div className="grid gap-2 md:grid-cols-3 text-xs text-muted-foreground">
                <div>当前值: <span className="text-foreground">{formatMetricValue(goal.currentValue, goal.metricUnit)}</span></div>
                <div>目标值: <span className="text-foreground">{formatMetricValue(goal.targetValue, goal.metricUnit)}</span></div>
                <div>阈值: <span className="text-foreground">{formatMetricValue(goal.alertThreshold, goal.metricUnit)}</span></div>
              </div>
              <div className="grid gap-2 md:grid-cols-3 text-xs text-muted-foreground">
                <div>周期: <span className="text-foreground">{goal.periodStart} → {goal.periodEnd}</span></div>
                <div>方向: <span className="text-foreground">{goal.targetDirection}</span></div>
                <div>最近检查: <span className="text-foreground">{ageFromNow(goal.lastCheckedAt)}</span></div>
              </div>
              {goal.notes && <p className="text-xs text-muted-foreground">备注: {goal.notes}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-1.5">
            <Activity className="h-4 w-4 text-orange-500" />Guardian 观察项
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.watchItems.length === 0 && <p className="text-sm text-muted-foreground">暂无观察项。</p>}
          {data.watchItems.map((item) => (
            <div key={item.id} className="border rounded p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-sm">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.watchType} · {item.status}{item.entityRef ? ` · ${item.entityRef}` : ''}</p>
                </div>
                <WatchPriorityBadge priority={item.priority} />
              </div>
              {item.description && <p className="text-sm text-muted-foreground">{item.description}</p>}
              <div className="grid gap-2 md:grid-cols-4 text-xs text-muted-foreground">
                <div>首次发现: <span className="text-foreground">{ageFromNow(item.firstSeenAt)}</span></div>
                <div>最近检查: <span className="text-foreground">{ageFromNow(item.lastSeenAt)}</span></div>
                <div>检查次数: <span className="text-foreground">{item.checkCount}</span></div>
                <div>动作次数: <span className="text-foreground">{item.actionCount}</span></div>
              </div>
              {item.resolution && <p className="text-xs text-muted-foreground">处理结果: {item.resolution}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">最近 Checkpoints</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.recentCheckpoints.length === 0 && <p className="text-sm text-muted-foreground">暂无 checkpoint。</p>}
            {data.recentCheckpoints.map((checkpoint) => (
              <div key={checkpoint.id} className="border rounded p-2.5 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{checkpoint.goalNameZh?.trim() || checkpoint.goalName || checkpoint.goalCode || checkpoint.goalId}</p>
                  <GoalStatusBadge status={checkpoint.status} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatMetricValue(checkpoint.actualValue)} / {formatMetricValue(checkpoint.targetValue)} · {checkpoint.triggeredBy} · {ageFromNow(checkpoint.recordedAt)}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">最近 Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.recentActions.length === 0 && <p className="text-sm text-muted-foreground">暂无 action。</p>}
            {data.recentActions.map((action) => (
              <div key={action.id} className="border rounded p-2.5 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{action.goalNameZh?.trim() || action.goalName || action.goalCode || action.goalId}</p>
                  <Badge variant="outline" className="text-[10px]">{action.actionType}</Badge>
                </div>
                <p className="text-sm mt-1">{action.description}</p>
                <p className="text-xs text-muted-foreground mt-1">{action.skillName ? `${action.skillName} · ` : ''}{ageFromNow(action.createdAt)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function HeartbeatTab({ tenantId }: { tenantId: string | null }) {
  const [heartbeats, setHeartbeats] = useState<HeartbeatRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<HeartbeatRecord[]>(buildBrainApiPath(tenantId, '/heartbeats'));
      setHeartbeats(result ?? []);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await api.post(buildBrainApiPath(tenantId, '/heartbeat'), {});
      await load();
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={triggering}
          onClick={() => void handleTrigger()}
        >
          {triggering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
          手动触发心跳
        </Button>
      </div>

      {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />}
      {!loading && heartbeats.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">暂无心跳记录</p>
      )}
      {heartbeats.map((hb) => (
        <Card key={hb.id}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{ageFromNow(hb.createdAt)}</CardTitle>
              <HealthBadge health={hb.analysis?.overallHealth ?? 'unknown'} />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {hb.analysis?.insights?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">洞察</p>
                <ul className="space-y-0.5">
                  {hb.analysis.insights.map((ins, i) => (
                    <li key={i} className="text-xs">• {ins}</li>
                  ))}
                </ul>
              </div>
            )}
            {hb.analysis?.suggestedActions?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">建议行动</p>
                <ul className="space-y-0.5">
                  {hb.analysis.suggestedActions.map((a, i) => (
                    <li key={i} className="text-xs text-blue-600 dark:text-blue-400">→ {a}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Weekly Report Tab (L8)
// ═══════════════════════════════════════════════════════════════

function WeeklyReportTab({ tenantId, canGenerate }: { tenantId: string | null; canGenerate: boolean }) {
  const [history, setHistory] = useState<WeeklyReport[]>([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<WeeklyReport[]>(`${buildBrainReportApiPath(tenantId, '/weekly/history')}?weeks=6`);
      setHistory(result ?? []);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.get(buildBrainReportApiPath(tenantId, '/weekly'));
      await load();
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={generating || !canGenerate}
          onClick={() => void handleGenerate()}
        >
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
          {canGenerate ? '生成本周周报' : '只读历史'}
        </Button>
      </div>

      {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />}
      {!loading && history.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">暂无周报。点击「生成本周周报」开始。</p>
      )}
      {history.map((report) => (
        <Card key={report.weekStart}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              周报 · {report.weekStart}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Metrics */}
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { label: '任务运行', value: report.metrics.automationsRan },
                { label: '成功', value: report.metrics.automationsSuccess },
                { label: '失败', value: report.metrics.automationsFailed },
                { label: '心跳次数', value: report.metrics.heartbeatsRan },
                { label: '事项创建', value: report.metrics.looseEndsCreated },
                { label: '事项解决', value: report.metrics.looseEndsResolved },
              ].map(({ label, value }) => (
                <div key={label} className="border rounded p-2">
                  <p className="text-lg font-bold">{value}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
            {/* LLM Summary */}
            {report.summaryLlm && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">本周总结</p>
                <p className="text-sm">{report.summaryLlm}</p>
              </div>
            )}
            {report.recommendations && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">改进建议</p>
                <p className="text-sm text-blue-600 dark:text-blue-400">{report.recommendations}</p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  BrainSoulPage
// ═══════════════════════════════════════════════════════════════

export default function BrainSoulPage() {
  const [searchParams] = useSearchParams();
  const scopedTenantId = searchParams.get('tenantId');
  const scopedTenantName = searchParams.get('tenantName') ?? '';
  const isPlatformView = Boolean(scopedTenantId);

  const [soul, setSoul] = useState<BrainSoul | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('soul');

  const loadSoul = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<BrainSoul>(buildBrainApiPath(scopedTenantId, '/soul'));
      setSoul(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [scopedTenantId]);

  useEffect(() => { void loadSoul(); }, [loadSoul]);

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'soul', label: 'Soul 状态', icon: <Brain className="h-4 w-4" /> },
    { id: 'guardian', label: 'Guardian 目标', icon: <Zap className="h-4 w-4" /> },
    { id: 'heartbeat', label: '心跳历史', icon: <Activity className="h-4 w-4" /> },
    { id: 'report', label: '自我周报', icon: <BarChart3 className="h-4 w-4" /> },
  ];

  const titleTenant = soul?.tenantName ?? scopedTenantName;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-purple-500" />
            {titleTenant ? `Brain Soul · ${titleTenant}` : 'Brain Soul'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isPlatformView
              ? '平台管理员跨租户查看：Soul、Guardian 目标、心跳与自评历史'
              : 'Brain 的记忆、洞察、自动化建议与自评报告'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadSoul()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded p-3">
          {error}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'soul' && (
        loading
          ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          : soul
            ? <SoulTab soul={soul} onRefresh={loadSoul} readOnly={isPlatformView} />
            : null
      )}
      {activeTab === 'guardian' && <GuardianTab tenantId={scopedTenantId} />}
      {activeTab === 'heartbeat' && <HeartbeatTab tenantId={scopedTenantId} />}
      {activeTab === 'report' && <WeeklyReportTab tenantId={scopedTenantId} canGenerate={!isPlatformView} />}
    </div>
  );
}
